<template>
  <div class="nav-root">
    <header ref="bar" class="header" :class="{ 'is-scrolled': scrolled, 'is-menu-open': menuOpen }">
      <router-link to="/" class="header__logo" data-nav-in aria-label="Coppados, inicio" @click.native="onLogo">
        <img src="../assets/logotipo-short.webp" alt="Coppados">
      </router-link>

      <nav class="header__nav" aria-label="Principal">
        <a
          v-for="link in links"
          :key="link.label"
          :href="link.href"
          class="header__link"
          data-nav-in
          @click.prevent="go(link)"
        >
          <span class="roll"><span :data-text="link.label">{{ link.label }}</span></span>
        </a>
      </nav>

      <div class="header__actions">
        <a v-magnetic="0.25" href="/#events" class="pill header__tickets" data-nav-in @click.prevent="go(ticketsLink)">
          <span class="roll"><span data-text="Get tickets">Get tickets</span></span>
        </a>
        <button
          ref="menuButton"
          class="header__menu-btn"
          :class="{ 'is-open': menuOpen }"
          type="button"
          data-nav-in
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-controls="mobile-menu"
          @click="toggleMenu"
        >
          <span class="header__menu-label">
            <span>Menu</span>
            <span>Close</span>
          </span>
        </button>
      </div>
    </header>

    <div
      id="mobile-menu"
      ref="menu"
      class="menu"
      :class="{ 'is-visible': menuVisible }"
      role="dialog"
      aria-modal="true"
      aria-label="Menú"
      @keydown.esc="closeMenu"
    >
      <div ref="mask" class="menu__mask">
        <div ref="counter" class="menu__panel">
          <nav class="menu__links">
            <a
              v-for="(link, i) in links"
              :key="link.label"
              :href="link.href"
              class="menu__link"
              @click.prevent="go(link)"
            >
              <span class="menu__link-mask">
                <span class="menu__link-text" data-menu-link>
                  {{ link.label }}<sup>0{{ i + 1 }}</sup>
                </span>
              </span>
            </a>
          </nav>
          <div class="menu__footer" data-menu-fade>
            <span>Hecho en Rosario</span>
            <a href="https://www.instagram.com/coppados/" target="_blank" rel="noopener">Instagram ↗</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/animations/gsap'
import { setupCircle, tweenCircle, MIN_SCALE } from '@/animations/circleReveal'
import { lockScroll, unlockScroll, scrollToTarget } from '@/animations/smoothScroll'

export default {
  name: 'NavigationBar',
  data() {
    return {
      scrolled: false,
      hidden: false,
      menuOpen: false,
      menuVisible: false,
      links: [
        { label: 'Music', href: '/#music', hash: '#music' },
        { label: 'Events', href: '/#events', hash: '#events' },
        { label: 'Artists', href: '/#artists', hash: '#artists' },
        { label: 'Shop', href: '/shop', path: '/shop' }
      ],
      ticketsLink: { hash: '#events' }
    }
  },
  watch: {
    $route() {
      if (this.menuOpen) this.closeMenu(true)
      this.setHidden(false)
    }
  },
  mounted() {
    this.trigger = ScrollTrigger.create({
      start: 0,
      end: 'max',
      onUpdate: (self) => {
        const y = self.scroll()
        this.scrolled = y > 40
        this.setHidden(self.direction === 1 && y > 240 && !this.menuOpen)
      }
    })
  },
  beforeDestroy() {
    if (this.trigger) this.trigger.kill()
    if (this.menuOpen) unlockScroll()
  },
  methods: {
    setHidden(hidden) {
      if (hidden === this.hidden) return
      this.hidden = hidden
      gsap.to(this.$refs.bar, {
        yPercent: hidden ? -100 : 0,
        duration: prefersReducedMotion() ? 0 : 0.5,
        ease: hidden ? 'power2.in' : 'expo.out'
      })
    },
    onLogo() {
      if (this.menuOpen) this.closeMenu()
      if (this.$route.path === '/') scrollToTarget(document.body)
    },
    go(link) {
      if (this.menuOpen) this.closeMenu()
      if (link.path) {
        if (this.$route.path !== link.path) this.$router.push(link.path)
        return
      }
      if (this.$route.path === '/') {
        scrollToTarget(link.hash)
      } else {
        this.$router.push({ path: '/', hash: link.hash })
      }
    },
    toggleMenu() {
      if (this.menuOpen) this.closeMenu()
      else this.openMenu()
    },
    openMenu() {
      this.menuOpen = true
      this.menuVisible = true
      lockScroll()
      this.$nextTick(() => {
        const btn = this.$refs.menuButton.getBoundingClientRect()
        const origin = { x: btn.left + btn.width / 2, y: btn.top + btn.height / 2 }
        const area = { width: window.innerWidth, height: window.innerHeight }
        const links = this.$refs.menu.querySelectorAll('[data-menu-link]')
        const fades = this.$refs.menu.querySelectorAll('[data-menu-fade]')
        if (this.timeline) this.timeline.kill()

        if (prefersReducedMotion()) {
          this.circle = setupCircle(this.$refs.mask, this.$refs.counter, area, origin, 1)
          this.timeline = gsap.timeline()
          return
        }

        const from = this.circle ? this.circle.state.scale : MIN_SCALE
        this.circle = setupCircle(this.$refs.mask, this.$refs.counter, area, origin, from)
        this.timeline = gsap.timeline()
          .add(tweenCircle(this.circle, 1, { duration: 1.1 }))
          .fromTo(links, { yPercent: 110 }, { yPercent: 0, duration: 1, stagger: 0.06, ease: 'expo.out' }, 0.3)
          .fromTo(fades, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8, ease: 'expo.out' }, 0.6)
        const firstLink = this.$refs.menu.querySelector('.menu__link')
        if (firstLink) firstLink.focus({ preventScroll: true })
      })
    },
    closeMenu(immediate = false) {
      if (!this.menuOpen) return
      this.menuOpen = false
      const finish = () => {
        this.menuVisible = false
        this.circle = null
        unlockScroll()
      }
      if (this.timeline) this.timeline.kill()
      if (immediate === true || prefersReducedMotion() || !this.circle) {
        finish()
        return
      }
      const links = this.$refs.menu.querySelectorAll('[data-menu-link]')
      const fades = this.$refs.menu.querySelectorAll('[data-menu-fade]')
      this.timeline = gsap.timeline({ onComplete: finish })
        .to(links, { yPercent: -110, duration: 0.4, stagger: 0.03, ease: 'power2.in' }, 0)
        .to(fades, { opacity: 0, duration: 0.3 }, 0)
        .add(tweenCircle(this.circle, MIN_SCALE, { duration: 0.8 }), 0.15)
    }
  }
}
</script>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-header);
  height: var(--header-h);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 2rem;
  padding: 0 var(--gutter);
  color: #fff;
  transition: background-color 0.5s var(--ease-out);
}

.header::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: var(--c-line);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.9s var(--ease-out);
}

.header.is-scrolled {
  background-color: rgba(0, 0, 0, 0.92);
}

.header.is-scrolled::after {
  transform: scaleX(1);
}

.header.is-menu-open {
  background-color: transparent;
}

.header.is-menu-open::after {
  transform: scaleX(0);
}

.header__logo {
  justify-self: start;
  display: block;
}

.header__logo img {
  width: clamp(7rem, 5rem + 5vw, 11.5rem);
  height: auto;
}

.header__nav {
  display: flex;
  gap: clamp(2rem, 1rem + 3vw, 5rem);
}

.header__link {
  position: relative;
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  --roll-color: var(--c-accent);
}

.header__actions {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header__menu-btn {
  display: none;
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.5rem 0;
  position: relative;
  z-index: 2;
}

.header__menu-label {
  display: grid;
  overflow: hidden;
  height: 1.2em;
  line-height: 1.2;
}

.header__menu-label > span {
  grid-area: 1 / 1;
  transition: transform 0.6s var(--ease-out);
}

.header__menu-label > span:last-child {
  transform: translateY(110%);
}

.header__menu-btn.is-open .header__menu-label > span:first-child {
  transform: translateY(-110%);
}

.header__menu-btn.is-open .header__menu-label > span:last-child {
  transform: translateY(0);
}

/* ---------- Menú mobile ---------- */

.menu {
  position: fixed;
  inset: 0;
  z-index: calc(var(--z-header) - 1);
  visibility: hidden;
  pointer-events: none;
}

.menu.is-visible {
  visibility: visible;
  pointer-events: auto;
}

.menu__panel {
  background: var(--c-accent);
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: calc(var(--header-h) + 2rem) var(--gutter) 2rem;
}

.menu__links {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.menu__link {
  display: block;
  font-size: clamp(3rem, 1rem + 9vh, 6rem);
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: -0.02em;
}

.menu__link-mask {
  display: block;
  overflow: hidden;
  padding: 0 0.15em 0.08em 0;
}

.menu__link-text {
  display: inline-block;
}

.menu__link sup {
  font-size: 0.22em;
  vertical-align: top;
  margin-left: 0.4em;
  letter-spacing: 0.1em;
  opacity: 0.8;
}

.menu__footer {
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-small);
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

@media (max-width: 900px) {
  .header {
    grid-template-columns: 1fr auto;
  }

  .header__nav,
  .header__tickets {
    display: none;
  }

  .header__menu-btn {
    display: block;
  }
}
</style>
